/**
 * FAA Report Models (Backend API)
 *
 * TypeScript interfaces matching the backend FaaReport model for API communication.
 * These interfaces support real API communication for FAA compliance reports.
 *
 * Note: For UI display models, use FaaGeneratedReport from report.model.ts
 * This file contains backend-specific interfaces for API requests/responses.
 *
 * Reference: /agent-os/specs/2026-01-15-faa-reports/spec.md
 */

// Import shared types from report.model.ts
import { FaaReportOptions, ReportStatus } from './report.model';

// Re-export for convenience
export { FaaReportOptions };

// ============================================
// STATUS TYPE (Backend-specific naming)
// ============================================

/**
 * FAA Report status values matching backend enum
 * Same as ReportStatus but explicitly typed for FAA reports
 */
export type FaaReportStatus = ReportStatus;

// ============================================
// FAA REPORT INTERFACE (Backend Model)
// ============================================

/**
 * FAA Report model matching backend response structure
 * This is the complete model returned from the API
 */
export interface FaaReport {
  id: string;
  organizationId: string;
  name: string;
  reportType: string;
  status: FaaReportStatus;
  reportUrl?: string;
  downloadUrl?: string; // Pre-signed URL for download (only present when fetching single report)
  startDate: string;
  endDate: string;
  options: FaaReportOptions;
  generationProgress: number;
  recordCount?: number;
  fileSize?: string;
  submittedAt?: string;
  submittedTo?: string;
  errorMessage?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

// ============================================
// API REQUEST/RESPONSE INTERFACES
// ============================================

/**
 * Request body for creating a new FAA report
 */
export interface CreateFaaReportRequest {
  name: string;
  startDate: string;
  endDate: string;
  options: FaaReportOptions;
}

/**
 * Request body for updating an FAA report
 */
export interface UpdateFaaReportRequest {
  name?: string;
  options?: FaaReportOptions;
}

/**
 * Request body for submitting an FAA report
 */
export interface SubmitFaaReportRequest {
  email: string;
}

/**
 * Paginated list response for FAA reports
 */
export interface FaaReportListResponse {
  reports: FaaReport[];
  total: number;
  page: number;
  limit: number;
}

/**
 * API response wrapper
 */
export interface FaaReportApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

// ============================================
// WEBSOCKET EVENT PAYLOADS
// ============================================

/**
 * WebSocket event payload for faa-report:progress
 */
export interface FaaReportProgressEvent {
  reportId: string;
  progress: number;
  status: 'generating';
}

/**
 * WebSocket event payload for faa-report:complete
 */
export interface FaaReportCompleteEvent {
  reportId: string;
  status: 'ready';
  url: string;
  recordCount: number;
  fileSize: string;
}

/**
 * WebSocket event payload for faa-report:failed
 */
export interface FaaReportFailedEvent {
  reportId: string;
  status: 'failed';
  error: string;
}

/**
 * Union type for all FAA report WebSocket events
 */
export type FaaReportWebSocketEvent =
  | FaaReportProgressEvent
  | FaaReportCompleteEvent
  | FaaReportFailedEvent;

// ============================================
// BACKEND REPORT TYPE OPTIONS
// ============================================

/**
 * Available FAA report types for backend API
 * Note: 'complete_faa' is the default backend value
 */
export const FAA_REPORT_TYPE_VALUES = [
  { value: 'complete_faa', label: 'Complete FAA Report' },
  { value: 'part137', label: 'Part 137 Operations' },
  { value: 'part107', label: 'Part 107 Compliance' },
  { value: 'waiver', label: 'Waiver Operations' },
  { value: 'incident', label: 'Incident Reports' }
] as const;

export type FaaReportType = typeof FAA_REPORT_TYPE_VALUES[number]['value'];

/**
 * Pre-generation warning payload (FAA §F.2.d).
 *
 * Which records in a would-be filing carry aircraft with no flight count and
 * would therefore be filed as 1. Mirrors `FaaReportMissingCountSummary` in the
 * Hub, which derives it from the report's own record-scope query and floor
 * function so the warning cannot disagree with what actually gets filed.
 */
export interface FaaReportMissingCountSummary {
  /** Records the filing would cover, after the status + date filter. */
  totalRecords: number;
  /** Of those, how many have at least one aircraft that will be floored to 1. */
  recordsMissingCount: number;
  /** Individual aircraft rows that will be floored (>= recordsMissingCount). */
  flightsMissingCount: number;
  records: Array<{
    sprayRecordId: string;
    applicationDate: string;
    aircraft: string[];
  }>;
}
